import { Question, QuestionMastery } from '../types';

/**
 * Calculates due questions for spaced repetition review based on nextReviewDue <= now.
 */
export function getDueQuestions(
  masteryList: QuestionMastery[] = [],
  allQuestions: Question[] = []
): { dueQuestions: Question[]; dueMasteryMap: Record<string, QuestionMastery> } {
  const safeMastery = Array.isArray(masteryList) ? masteryList : [];
  const safeQuestions = Array.isArray(allQuestions) ? allQuestions : [];

  const nowMs = Date.now();
  const dueMasteryMap: Record<string, QuestionMastery> = {};

  const dueMastery = safeMastery.filter(m => {
    if (!m || !m.nextReviewDue) return false;
    const dueMs = new Date(m.nextReviewDue).getTime();
    if (!isNaN(dueMs) && dueMs <= nowMs) {
      if (m.questionId) {
        dueMasteryMap[m.questionId] = m;
      }
      return true;
    }
    return false;
  });

  const dueQuestionIds = new Set(dueMastery.map(m => m.questionId).filter(Boolean));
  const dueQuestions = safeQuestions.filter(q => q && q.id && dueQuestionIds.has(q.id));

  return { dueQuestions, dueMasteryMap };
}

/**
 * Generates an adaptively weighted question set prioritizing weaker domains and incorrect items.
 */
export function generateSmartPracticeQuestions(
  masteryList: QuestionMastery[] = [],
  allQuestions: Question[] = [],
  targetCount: number = 10
): Question[] {
  const safeMastery = Array.isArray(masteryList) ? masteryList : [];
  const safeQuestions = Array.isArray(allQuestions) ? allQuestions : [];

  if (safeQuestions.length === 0) return [];

  // Group stats by domain
  const domainStats: Record<string, { correct: number; total: number }> = {};
  const masteryMap: Record<string, QuestionMastery> = {};

  for (const m of safeMastery) {
    if (m && m.questionId) {
      masteryMap[m.questionId] = m;
    }
  }

  for (const q of safeQuestions) {
    if (!q) continue;
    const domain = q.domain || 'General';
    if (!domainStats[domain]) {
      domainStats[domain] = { correct: 0, total: 0 };
    }
    const m = q.id ? masteryMap[q.id] : undefined;
    if (m) {
      domainStats[domain].correct += m.timesCorrect || 0;
      domainStats[domain].total += (m.timesCorrect || 0) + (m.timesIncorrect || 0);
    }
  }

  // Calculate scores for each question
  const scored = safeQuestions.filter(Boolean).map(q => {
    const domain = q.domain || 'General';
    const dStat = domainStats[domain];
    let domainAccuracy = 0.5;
    if (dStat && dStat.total > 0) {
      domainAccuracy = dStat.correct / dStat.total;
    }

    // Domain weight: lower accuracy -> higher weight (range ~0.3 to 1.3)
    const domainWeight = Math.max(0.2, 1.0 - domainAccuracy + 0.3);

    const m = q.id ? masteryMap[q.id] : undefined;
    let questionWeight = 1.0;
    if (m) {
      if (m.lastResult === 'incorrect') {
        questionWeight = 2.8; // High priority for questions missed recently
      } else if (m.timesCorrect > 2) {
        questionWeight = 0.35; // Lower priority for mastered items
      } else {
        questionWeight = 0.8;
      }
    } else {
      questionWeight = 1.4; // Unattempted items priority
    }

    // Small random factor (0.85 - 1.15) for variation
    const randomFactor = 0.85 + Math.random() * 0.3;
    const finalScore = domainWeight * questionWeight * randomFactor;

    return { question: q, score: finalScore };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, Math.min(targetCount, scored.length)).map(s => s.question);
}
